/**
 * View-event effect for the NCCN evaluation panel.
 * @module useNccnPanelViewTracking
 */
import { MutableRefObject, useEffect } from "react";
import { NCCNEvaluationRecord } from "../../../data-model/nccnEvaluation";
import {
  EvaluationItem,
  EvaluationResult,
} from "../../../data-model/nccnGuidelines";
import { getGuidelineReviewVersion } from "../analytics/guidelineReviewVersion";
import { MedicalNecessityAnalytics } from "../analytics/useMedicalNecessityAnalytics";

/** Inputs for {@link useNccnPanelViewTracking}. */
export interface UseNccnPanelViewTrackingArgs {
  analytics: MedicalNecessityAnalytics;
  orderId: string;
  isLoading: boolean;
  hasEvaluation: boolean;
  phase: "stepthrough" | "summary";
  currentGuideline: NCCNEvaluationRecord | undefined;
  currentItem: EvaluationItem | undefined;
  guidelineIndex: number;
  criteriaIndex: number;
  guidelineDisplayPosition: number;
  isSummaryStep: boolean;
  /** All guideline records (for summary counts). */
  guidelines: NCCNEvaluationRecord[];
  /** Order-level AI verdict (for the summary view). */
  aiOrderVerdict: EvaluationResult | null | undefined;
  /** Order-level human verdict (for the summary view). */
  humanOrderVerdict: EvaluationResult | null | undefined;
  pendingActionIdRef: MutableRefObject<string | undefined>;
}

/**
 * Fires guideline / criterion / verdict / summary view events on genuine entry.
 *
 * @param args - Panel position, analytics, and pending action ID ref
 */
export const useNccnPanelViewTracking = ({
  analytics,
  orderId,
  isLoading,
  hasEvaluation,
  phase,
  currentGuideline,
  currentItem,
  guidelineIndex,
  criteriaIndex,
  guidelineDisplayPosition,
  isSummaryStep,
  guidelines,
  aiOrderVerdict,
  humanOrderVerdict,
  pendingActionIdRef,
}: UseNccnPanelViewTrackingArgs): void => {
  useEffect(() => {
    if (isLoading || !hasEvaluation) return;

    if (phase === "summary") {
      const actionId = pendingActionIdRef.current;
      analytics.trackSummaryViewed(
        guidelines,
        aiOrderVerdict,
        humanOrderVerdict,
        `summary:${orderId}`,
        actionId,
        pendingActionIdRef,
      );
      return;
    }

    if (!currentGuideline) return;

    const guidelineId = currentGuideline.guideline_id ?? "";
    const reviewVersion = getGuidelineReviewVersion(
      analytics.analysisId,
      guidelineId,
    );
    // Peek only — trackers clear the ref after a real (non-deduped) emit.
    const actionId = pendingActionIdRef.current;

    analytics.trackGuidelineViewed(
      currentGuideline,
      guidelineDisplayPosition,
      `guideline:${guidelineId}:v${reviewVersion}:${guidelineIndex}`,
      actionId,
      pendingActionIdRef,
    );

    if (isSummaryStep) {
      analytics.trackGuidelineVerdictViewed(
        currentGuideline,
        guidelineDisplayPosition,
        `verdict:${guidelineId}:v${reviewVersion}:${guidelineIndex}`,
        actionId,
        pendingActionIdRef,
      );
      return;
    }

    if (currentItem) {
      analytics.trackCriterionViewed(
        currentGuideline,
        guidelineDisplayPosition,
        currentItem,
        criteriaIndex + 1,
        `criterion:${guidelineId}:${currentItem.id}:v${reviewVersion}:${criteriaIndex}`,
        actionId,
        pendingActionIdRef,
      );
    }
  }, [
    analytics,
    aiOrderVerdict,
    criteriaIndex,
    currentGuideline,
    currentItem,
    guidelineDisplayPosition,
    guidelineIndex,
    guidelines,
    hasEvaluation,
    humanOrderVerdict,
    isLoading,
    isSummaryStep,
    orderId,
    pendingActionIdRef,
    phase,
  ]);
};
